import { useCallback, useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { faClose, faSliders } from '@fortawesome/pro-solid-svg-icons'
import { useMediaQuery, useMountEffect } from '@react-hookz/web'
import clsx from 'clsx'
import { debounce } from 'lodash-es'

import { Heading, LinkButton } from 'Components/_theme'
import { Icon } from 'Components/Icon/Icon'

import { setContainerWidth } from 'Utilities'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'
import { E2E_FILTER_COLUMN_CLOSE_BUTTON } from 'Constants/e2e'

import { FilterColumnProps } from './FilterColumn.types'

export const FilterColumn = (props: FilterColumnProps) => {
  const { children, columnRef, isOpen, onClose } = props

  const intl = useIntl()
  const containerRef = useRef<HTMLDivElement>(null)

  const classNames = clsx('FilterColumn', isOpen && 'FilterColumn--is-open')

  const isDesktop = useMediaQuery(`(min-width: ${PORTAL_BREAKPOINTS.XXL})`)

  const setFilterColumnWidth = useCallback(() => {
    setContainerWidth(containerRef, columnRef, isDesktop)
  }, [columnRef, isDesktop])

  useMountEffect(() => {
    setFilterColumnWidth()
  })

  useEffect(() => {
    const debouncedUpdateContainerWidth = debounce(setFilterColumnWidth, 250)

    window.addEventListener('resize', debouncedUpdateContainerWidth)

    return () => {
      window.removeEventListener('resize', debouncedUpdateContainerWidth)
    }
  }, [setFilterColumnWidth])

  return (
    <div ref={containerRef} className={classNames}>
      <div className="FilterColumn-header">
        <Icon icon={faSliders} size={20} />

        <Heading size={5}>
          <FormattedMessage id="Filter_by" />
        </Heading>

        <div className="FilterColumn-header-close">
          <LinkButton
            aria-label={intl.formatMessage({ id: 'Close' })}
            data-e2e={E2E_FILTER_COLUMN_CLOSE_BUTTON}
            onClick={onClose}
          >
            <Icon icon={faClose} size={20} />
          </LinkButton>
        </div>
      </div>

      <div className="FilterColumn-content">{children}</div>
    </div>
  )
}
